import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
} from '@mui/material';
import styled from 'styled-components';

import { ReactComponent as BinIcon } from '@/assets/icons/bin.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import { getInitiativeById, getRoadmapInitiatives } from '@/features/canvas/api';
import {
  addInitiativeChild,
  addInitiativeParent,
  getInitiativeAncestors,
  getInitiativeChildren,
  getInitiativeDescendants,
  getInitiativeParents,
  removeInitiativeChild,
  removeInitiativeParent,
} from '@/features/canvas/api/initiative-relationships';
import {
  createRoadmapNode,
  deleteNode,
  getAllRoadmapNodes,
  importNewRoadmapInitiativeNode,
} from '@/features/canvas/api/node';
import { StyledSelect } from '@/features/canvas/components/StyledSelect';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { getRoadmapList } from '@/features/roadmaps/api';
import { useShowToast } from '@/hooks/useShowToast';
import { components } from '@/types/api';

type RelationshipType = 'assign-parent' | 'assign-child' | 'create-child' | null;

const RelationshipsSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showToast } = useShowToast();
  const queryClient = useQueryClient();
  const {
    selectedNodeId,
    setSelectedNodeId,
    setEditingInitiativeId,
    editingInitiativeId,
    roadmap,
    filteredNodes,
    roadmapNodes,
    temporaryCreatedIds,
    sortedTimePeriods,
    sortedVerticalSections,
  } = useEditorContext();

  const [relationshipType, setRelationshipType] = useState<RelationshipType>(null);
  const [selectedRoadmapId, setSelectedRoadmapId] = useState<number | null>(null);
  const [availableInitiatives, setAvailableInitiatives] = useState<
    components['schemas']['Initiative'][]
  >([]);
  const [isLoadingInitiatives, setIsLoadingInitiatives] = useState(false);
  const [excludedInitiativeIds, setExcludedInitiativeIds] = useState<number[]>([]);

  // Find the selected initiative - support both selectedNodeId and editingInitiativeId
  const selectedInitiative = useMemo(() => {
    // If we have an editingInitiativeId, we need to fetch that initiative
    // This will be handled by the query below
    if (editingInitiativeId) {
      return { id: editingInitiativeId }; // Minimal object to trigger the query
    }

    // Otherwise, get initiative from selectedNodeId
    if (!selectedNodeId) return null;

    // First try to find in filteredNodes (visible on canvas)
    let node = filteredNodes.find(n => n.id === selectedNodeId);

    // Only use roadmapNodes fallback if the node doesn't have a temporary ID
    // Temporary nodes may have incomplete initiative data
    const isTemporaryNode = selectedNodeId && temporaryCreatedIds.has(selectedNodeId);

    // If not found in filteredNodes, fallback to roadmapNodes (unfiltered)
    // This handles the case where a node was hidden by KON-145 after becoming a child
    if (!node && roadmapNodes && !isTemporaryNode) {
      const roadmapNode = roadmapNodes.find(n => n.id === selectedNodeId);
      if (roadmapNode) {
        // Convert to FilteredNodes type structure for consistency
        node = {
          ...roadmapNode,
          color: '',
          shape: 'circle' as const,
          size: undefined,
        };
      }
    }

    return node?.initiatives || null;
  }, [selectedNodeId, editingInitiativeId, filteredNodes, roadmapNodes, temporaryCreatedIds]);

  // Determine the initiative ID to use
  const initiativeId = editingInitiativeId || selectedInitiative?.id;

  // Fetch all roadmaps
  const { data: roadmaps } = useQuery('getRoadmapList', getRoadmapList, {
    staleTime: 0,
    refetchOnMount: 'always',
    onError: () => {
      showToast('error', t('roadmaps.fetch_error'));
    },
  });

  // Fetch parents
  const { data: parents, isLoading: isLoadingParents } = useQuery(
    ['getInitiativeParents', initiativeId],
    () => getInitiativeParents({ initiativeId: initiativeId! }),
    {
      enabled: !!initiativeId,
      onError: () => {
        // Failed to fetch parents
      },
    }
  );

  // Fetch children
  const { data: children, isLoading: isLoadingChildren } = useQuery(
    ['getInitiativeChildren', initiativeId],
    () => getInitiativeChildren({ initiativeId: initiativeId! }),
    {
      enabled: !!initiativeId,
      onError: () => {
        // Failed to fetch children
      },
    }
  );

  // Mutations for adding relationships
  const { mutateAsync: addParent, isLoading: isAddingParent } = useMutation(addInitiativeParent, {
    onSuccess: () => {
      showToast('success', t('editor.sidebar.relationships.parent_added'));
      void queryClient.invalidateQueries(['getInitiativeParents']);
      // Invalidate main initiative data to update relationships section immediately
      void queryClient.invalidateQueries([
        getInitiativeById.name,
        selectedNodeId,
        editingInitiativeId,
      ]);
      // Also invalidate the parent's data to show the new child relationship
      void queryClient.invalidateQueries([getInitiativeById.name]);
      // Invalidate roadmap nodes to update canvas visibility
      void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
      setRelationshipType(null);
      setSelectedRoadmapId(null);
    },
    onError: () => {
      showToast('error', t('editor.sidebar.relationships.parent_add_error'));
    },
  });

  const { mutateAsync: addChild, isLoading: isAddingChild } = useMutation(
    async ({ childId }: { childId: number }) => {
      // First add the child relationship
      await addInitiativeChild({
        initiativeId: initiativeId!,
        childId,
      });

      // Check if child initiative already has a node in current roadmap
      const childHasNodeInCurrentRoadmap = roadmapNodes?.some(
        node => node.initiatives?.id === childId
      );

      if (!childHasNodeInCurrentRoadmap) {
        // Cross-roadmap child - create a node for it in current roadmap
        // Find parent node position
        let parentNode: { sectionId: number; timePeriodId: number; x: number; y: number };

        let foundNode;
        if (selectedNodeId) {
          foundNode = filteredNodes.find(n => n.id === selectedNodeId);
        } else if (editingInitiativeId) {
          foundNode = roadmapNodes?.find(n => n.initiatives?.id === editingInitiativeId);
        }

        if (foundNode) {
          parentNode = {
            x: foundNode.x || 50,
            y: foundNode.y || 50,
            sectionId: foundNode.sectionId || 1,
            timePeriodId: foundNode.timePeriodId || 1,
          };
        } else {
          // Fallback to default position
          const firstTimePeriodId = sortedTimePeriods[0]?.id || 1;
          const firstSectionId = sortedVerticalSections[0]?.id || 1;

          parentNode = {
            x: 50,
            y: 50,
            sectionId: firstSectionId,
            timePeriodId: firstTimePeriodId,
          };
        }

        // Create node for existing initiative in current roadmap
        await importNewRoadmapInitiativeNode({
          roadmapId: roadmap.id!,
          initiativeId: childId,
          nodeInfo: {
            x: parentNode.x + 50, // Offset from parent
            y: parentNode.y + 50,
            sectionId: parentNode.sectionId,
            timePeriodId: parentNode.timePeriodId,
            foreignRoadmapId: selectedRoadmapId || roadmap.id!, // Original roadmap
          },
        });

        // Recursively create nodes for all descendants (grandchildren, etc.)
        const createNodesForDescendants = async (
          currentInitiativeId: number,
          baseX: number,
          baseY: number,
          depth = 0
        ) => {
          try {
            const descendants = await getInitiativeChildren({ initiativeId: currentInitiativeId });

            // Filter descendants that need nodes created
            const descendantsNeedingNodes = descendants.filter(descendant => {
              const hasNodeInCurrentRoadmap = roadmapNodes?.some(
                node => node.initiatives?.id === descendant.id
              );
              return !hasNodeInCurrentRoadmap && descendant.id;
            });

            // Create all nodes for descendants concurrently
            const nodeCreationPromises = descendantsNeedingNodes.map(async (descendant, index) => {
              // Calculate position with offset from parent
              const offsetX = baseX + (depth + 1) * 60; // Spread horizontally by depth
              const offsetY = baseY + index * 40; // Spread vertically by child index

              // Create node for this descendant
              await importNewRoadmapInitiativeNode({
                roadmapId: roadmap.id!,
                initiativeId: descendant.id!,
                nodeInfo: {
                  x: offsetX,
                  y: offsetY,
                  sectionId: parentNode.sectionId,
                  timePeriodId: parentNode.timePeriodId,
                  foreignRoadmapId: selectedRoadmapId || roadmap.id!,
                },
              });

              return { descendant, offsetX, offsetY };
            });

            // Wait for all nodes to be created
            const createdNodes = await Promise.all(nodeCreationPromises);

            // Recursively create nodes for each descendant's children
            const recursionPromises = createdNodes.map(({ descendant, offsetX, offsetY }) =>
              createNodesForDescendants(descendant.id!, offsetX, offsetY, depth + 1)
            );

            await Promise.all(recursionPromises);
          } catch (error) {
            // If we can't fetch children, skip this branch but don't fail the whole operation
          }
        };

        // Start recursion for the newly added child
        await createNodesForDescendants(childId, parentNode.x + 50, parentNode.y + 50);
      }

      return { childId };
    },
    {
      onSuccess: () => {
        showToast('success', t('editor.sidebar.relationships.child_added'));
        void queryClient.invalidateQueries(['getInitiativeChildren']);
        // Invalidate main initiative data to update relationships section immediately
        void queryClient.invalidateQueries([
          getInitiativeById.name,
          selectedNodeId,
          editingInitiativeId,
        ]);
        // Also invalidate the child's data to show the new parent relationship
        void queryClient.invalidateQueries([getInitiativeById.name]);
        // Invalidate nodes to update canvas visibility (KON-145)
        void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
        setRelationshipType(null);
        setSelectedRoadmapId(null);
      },
      onError: () => {
        showToast('error', t('editor.sidebar.relationships.child_add_error'));
      },
    }
  );

  const { mutateAsync: removeParent, isLoading: isRemovingParent } = useMutation(
    removeInitiativeParent,
    {
      onSuccess: () => {
        showToast('success', t('editor.sidebar.relationships.parent_removed'));
        void queryClient.invalidateQueries(['getInitiativeParents']);
        // Invalidate main initiative data to update relationships section immediately
        void queryClient.invalidateQueries([
          getInitiativeById.name,
          selectedNodeId,
          editingInitiativeId,
        ]);
        // Also invalidate the parent's data to remove the child relationship
        void queryClient.invalidateQueries([getInitiativeById.name]);
        // Invalidate roadmap nodes to update canvas visibility
        void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
      },
      onError: () => {
        showToast('error', t('editor.sidebar.relationships.parent_remove_error'));
      },
    }
  );

  const { mutateAsync: removeChild, isLoading: isRemovingChild } = useMutation(
    async ({ childId }: { childId: number }) => {
      // First remove the child relationship
      await removeInitiativeChild({
        initiativeId: initiativeId!,
        childId,
      });

      // Check if the child's node in this roadmap should be deleted
      const childNodeInCurrentRoadmap = roadmapNodes?.find(
        node => node.initiatives?.id === childId
      );

      if (!childNodeInCurrentRoadmap) {
        return { childId };
      }

      try {
        // Check if child initiative has any remaining parents in this roadmap
        const remainingParents = await getInitiativeParents({ initiativeId: childId });

        // Check if any of the remaining parents have nodes in this roadmap
        const hasParentInCurrentRoadmap = remainingParents.some(parent =>
          roadmapNodes?.some(node => node.initiatives?.id === parent.id)
        );

        if (hasParentInCurrentRoadmap) {
          return { childId };
        }

        // Child has no remaining parents in this roadmap
        // Check if this initiative was originally created in a different roadmap
        const childInitiative = await getInitiativeById({ id: childId });

        if (childInitiative.originNodeId) {
          // Check if the origin node exists in the current roadmap's nodes
          // If it doesn't, this means the initiative was created in another roadmap
          const originNodeInCurrentRoadmap = roadmapNodes?.some(
            node => node.id === childInitiative.originNodeId
          );

          if (!originNodeInCurrentRoadmap) {
            // This is a cross-roadmap imported node - safe to delete when orphaned

            // Recursively delete all descendant nodes that were also cross-roadmap imports
            const deleteNodesForDescendants = async (currentInitiativeId: number) => {
              try {
                const descendants = await getInitiativeChildren({
                  initiativeId: currentInitiativeId,
                });

                const deletePromises = descendants
                  .filter(descendant => {
                    const nodeInRoadmap = roadmapNodes?.find(
                      n => n.initiatives?.id === descendant.id
                    );
                    return nodeInRoadmap && descendant.id;
                  })
                  .map(async descendant => {
                    const nodeInRoadmap = roadmapNodes?.find(
                      n => n.initiatives?.id === descendant.id
                    );
                    if (nodeInRoadmap?.id && descendant.id) {
                      // Check if cross-roadmap import before deleting
                      const descInitiative = await getInitiativeById({ id: descendant.id });
                      if (descInitiative.originNodeId) {
                        const originInCurrentRoadmap = roadmapNodes?.some(
                          n => n.id === descInitiative.originNodeId
                        );
                        if (!originInCurrentRoadmap) {
                          await deleteNode({ id: nodeInRoadmap.id });
                        }
                      }
                    }
                    // Recurse to handle descendants of descendants
                    if (descendant.id) {
                      await deleteNodesForDescendants(descendant.id);
                    }
                  });

                await Promise.all(deletePromises);
              } catch (error) {
                // Skip silently if we can't fetch children
              }
            };

            // Delete all descendants first, then delete the direct child
            await deleteNodesForDescendants(childId);
            await deleteNode({ id: childNodeInCurrentRoadmap.id! });
          }
        }
      } catch (error) {
        // If we can't determine relationships or origin, err on the side of caution
        // and don't delete the node
      }

      return { childId };
    },
    {
      onSuccess: () => {
        showToast('success', t('editor.sidebar.relationships.child_removed'));
        void queryClient.invalidateQueries(['getInitiativeChildren']);
        // Invalidate main initiative data to update relationships section immediately
        void queryClient.invalidateQueries([
          getInitiativeById.name,
          selectedNodeId,
          editingInitiativeId,
        ]);
        // Also invalidate the child's data to remove the parent relationship
        void queryClient.invalidateQueries([getInitiativeById.name]);
        // Invalidate nodes to update canvas visibility (KON-145)
        void queryClient.invalidateQueries([getAllRoadmapNodes.name, roadmap.id]);
      },
      onError: () => {
        showToast('error', t('editor.sidebar.relationships.child_remove_error'));
      },
    }
  );

  const { mutateAsync: createChild, isLoading: isCreatingChild } = useMutation(
    async () => {
      if (!initiativeId || !roadmap.id) {
        return null;
      }

      // Find the current node to get section and time period
      // Handle both selectedNodeId (canvas selection) and editingInitiativeId (pencil icon) scenarios
      let currentNode;

      if (selectedNodeId) {
        // Standard case: node selected on canvas
        currentNode = filteredNodes.find(n => n.id === selectedNodeId);
      } else if (editingInitiativeId) {
        // Alternative case: editing a child initiative via pencil icon (no selectedNodeId)
        // Look in both filteredNodes and roadmapNodes since hidden children may not be in filteredNodes
        currentNode =
          filteredNodes.find(n => n.initiatives?.id === editingInitiativeId) ||
          roadmapNodes?.find(n => n.initiatives?.id === editingInitiativeId);

        // If not found in current arrays, check the query cache directly
        // This handles the case where a child was just created and cache is still updating
        if (!currentNode) {
          const cachedNodes = queryClient.getQueryData([getAllRoadmapNodes.name, roadmap.id]) as
            | components['schemas']['Node'][]
            | undefined;
          if (cachedNodes) {
            currentNode = cachedNodes.find(n => n.initiatives?.id === editingInitiativeId);
          }
        }
      }

      if (!currentNode) {
        // As a fallback for newly created children, use default values
        // This allows the operation to proceed even if the cache isn't fully updated
        const firstTimePeriodId = sortedTimePeriods[0]?.id || 1;
        const firstSectionId = sortedVerticalSections[0]?.id || 1;

        currentNode = {
          x: 50,
          y: 50,
          sectionId: firstSectionId,
          timePeriodId: firstTimePeriodId,
        };
      }

      // Step 1: Create a node in the roadmap with initiative
      // The node needs section and time period IDs
      const newNodeData = {
        x: (currentNode.x || 50) + 10, // Position near parent
        y: (currentNode.y || 50) + 10,
        title: t('editor.sidebar.relationships.new_child_title'),
        isVisible: true,
        sectionId: currentNode.sectionId || 1, // Use parent's section or default
        timePeriodId: currentNode.timePeriodId || 1, // Use parent's time period or default
        initiatives: {
          title: t('editor.sidebar.relationships.new_child_title'),
        },
      } as components['schemas']['Node'];

      const newNode = await createRoadmapNode({
        roadmapId: roadmap.id,
        newNode: newNodeData,
      });

      // The node creation also creates the initiative
      // Backend returns the initiative in the 'initiatives' property
      const newInitiativeId = newNode.initiatives?.id;

      if (newInitiativeId) {
        await addInitiativeChild({
          initiativeId,
          childId: newInitiativeId,
        });
      }

      // Ensure roadmap nodes cache is updated before mutation resolves
      await queryClient.refetchQueries([getAllRoadmapNodes.name, roadmap.id]);

      return newNode.initiatives;
    },
    {
      onSuccess: async () => {
        showToast('success', t('editor.sidebar.relationships.child_created'));

        // Invalidate queries and wait for them to complete
        // Note: roadmap nodes are already refetched in the mutation function
        await Promise.all([
          queryClient.invalidateQueries(['getInitiativeChildren']),
          queryClient.invalidateQueries([
            getInitiativeById.name,
            selectedNodeId,
            editingInitiativeId,
          ]),
          // Also invalidate all initiative data to show updated relationships
          queryClient.invalidateQueries([getInitiativeById.name]),
        ]);

        setRelationshipType(null);
      },
      onError: () => {
        showToast('error', t('editor.sidebar.relationships.child_create_error'));
      },
    }
  );

  // Get roadmap options sorted with current roadmap at top
  const roadmapOptions = useMemo(() => {
    if (!roadmaps) return [];

    const currentRoadmap = roadmaps.find(r => r.id === roadmap.id);
    const otherRoadmaps = roadmaps
      .filter(r => r.id !== roadmap.id)
      .sort((a, b) => a.title.localeCompare(b.title));

    const options = currentRoadmap ? [currentRoadmap, ...otherRoadmaps] : otherRoadmaps;

    return options.map(r => ({
      id: r.id!,
      title: r.id === roadmap.id ? `${r.title} (Current)` : r.title,
    }));
  }, [roadmaps, roadmap.id]);

  // Fetch excluded initiatives based on relationship type
  useEffect(() => {
    if (!initiativeId || !relationshipType || relationshipType === 'create-child') {
      setExcludedInitiativeIds([]);
      return;
    }

    const fetchExcludedInitiatives = async () => {
      try {
        let excluded: number[] = [initiativeId]; // Always exclude self

        if (relationshipType === 'assign-parent') {
          // When assigning parent, exclude all descendants
          const descendants = await getInitiativeDescendants({
            initiativeId,
          });
          excluded = [...excluded, ...descendants.map(d => d.id!)];
        } else if (relationshipType === 'assign-child') {
          // When assigning child, exclude all ancestors
          const ancestors = await getInitiativeAncestors({ initiativeId });
          excluded = [...excluded, ...ancestors.map(a => a.id!)];
        }

        setExcludedInitiativeIds(excluded);
      } catch (error) {
        console.error('Failed to fetch excluded initiatives:', error);
        // On error, at least exclude self
        setExcludedInitiativeIds([initiativeId]);
      }
    };

    void fetchExcludedInitiatives();
  }, [initiativeId, relationshipType]);

  // Load initiatives when roadmap is selected
  useEffect(() => {
    if (!selectedRoadmapId) {
      setAvailableInitiatives([]);
      return;
    }

    const loadInitiatives = async () => {
      setIsLoadingInitiatives(true);
      try {
        const initiatives = await getRoadmapInitiatives({ roadmapId: selectedRoadmapId });

        // Filter based on relationship type and excluded initiatives
        let filtered = initiatives.filter(i => !excludedInitiativeIds.includes(i.id!));

        if (relationshipType === 'assign-parent') {
          // Also filter out existing parents
          const parentIds = parents?.map(p => p.id) || [];
          filtered = filtered.filter(i => !parentIds.includes(i.id));
        } else if (relationshipType === 'assign-child') {
          // Also filter out existing children
          const childIds = children?.map(c => c.id) || [];
          filtered = filtered.filter(i => !childIds.includes(i.id));
        }

        setAvailableInitiatives(filtered.sort((a, b) => a.title.localeCompare(b.title)));
      } catch (error) {
        showToast('error', t('editor.sidebar.relationships.initiatives_load_error'));
      } finally {
        setIsLoadingInitiatives(false);
      }
    };

    void loadInitiatives();
  }, [
    selectedRoadmapId,
    relationshipType,
    selectedInitiative,
    parents,
    children,
    excludedInitiativeIds,
    showToast,
    t,
  ]);

  const handleInitiativeSelection = async (selectedInitiativeId: number) => {
    if (!initiativeId) return;

    if (relationshipType === 'assign-parent') {
      await addParent({
        initiativeId,
        parentId: selectedInitiativeId,
      });
    } else if (relationshipType === 'assign-child') {
      await addChild({
        childId: selectedInitiativeId,
      });
    }
  };

  const handleCreateNewChild = async () => {
    if (!initiativeId) return;
    await createChild();
  };

  const handleEditInitiative = (editInitiativeId: number) => {
    // Find the node that contains this initiative in the current roadmap
    const node = filteredNodes.find(n => n.initiatives?.id === editInitiativeId);

    if (node?.id) {
      // Initiative is in the current roadmap - select the node
      setSelectedNodeId(node.id);
      setEditingInitiativeId(null);
    } else {
      // Initiative is from another roadmap - edit directly without a node
      setSelectedNodeId(null);
      setEditingInitiativeId(editInitiativeId);
    }
  };

  if (!initiativeId) {
    return null;
  }

  const isLoading = isLoadingParents || isLoadingChildren;
  const isMutating =
    isAddingParent || isAddingChild || isRemovingParent || isRemovingChild || isCreatingChild;

  return (
    <SectionContainer>
      <SectionHeader>{t('editor.sidebar.relationships.title')}</SectionHeader>
      <Divider variant="fullWidth" sx={{ borderColor: theme.palette.brand.backgroundDialog }} />

      {isLoading && (
        <LoaderWrapper>
          <CircularProgress size={24} />
        </LoaderWrapper>
      )}

      {!isLoading && (
        <>
          {/* Relationship Type Selector */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <StyledRelationshipSelect
              value={relationshipType || ''}
              onChange={e => {
                const value = e.target.value as RelationshipType;
                if (value === 'create-child') {
                  // Immediately create child instead of setting relationship type
                  void handleCreateNewChild();
                } else {
                  setRelationshipType(value);
                }
                setSelectedRoadmapId(null);
              }}
              displayEmpty
              disabled={isMutating}
              renderValue={value => {
                if (!value) {
                  return t('editor.sidebar.relationships.select_relationship');
                }
                switch (value) {
                  case 'assign-parent':
                    return t('editor.sidebar.relationships.assign_parent');
                  case 'assign-child':
                    return t('editor.sidebar.relationships.assign_child');
                  case 'create-child':
                    return t('editor.sidebar.relationships.create_new_child');
                  default:
                    return '';
                }
              }}
            >
              <MenuItem value="assign-parent">
                {t('editor.sidebar.relationships.assign_parent')}
              </MenuItem>
              <MenuItem value="assign-child">
                {t('editor.sidebar.relationships.assign_child')}
              </MenuItem>
              <MenuItem value="create-child">
                {t('editor.sidebar.relationships.create_new_child')}
              </MenuItem>
            </StyledRelationshipSelect>
          </Box>

          {/* Roadmap Selector for assign parent/child */}
          {(relationshipType === 'assign-parent' || relationshipType === 'assign-child') && (
            <Box sx={{ mt: 2 }}>
              <StyledSelect
                placeholder={t('editor.sidebar.relationships.select_roadmap')}
                selectedId={selectedRoadmapId}
                options={roadmapOptions}
                onChange={value => setSelectedRoadmapId(value)}
              />
            </Box>
          )}

          {/* Initiative Selector */}
          {selectedRoadmapId && !isLoadingInitiatives && (
            <Box sx={{ mt: 2 }}>
              <StyledSelect
                placeholder={t('editor.sidebar.relationships.select_initiative')}
                selectedId={null}
                options={availableInitiatives.map(i => ({ id: i.id!, title: i.title }))}
                onChange={value => {
                  if (value && !isAddingParent && !isAddingChild) {
                    void handleInitiativeSelection(value);
                  }
                }}
              />
            </Box>
          )}

          {/* Loading initiatives */}
          {isLoadingInitiatives && (
            <LoaderWrapper>
              <CircularProgress size={24} />
            </LoaderWrapper>
          )}

          {/* Parents List */}
          {parents && parents.length > 0 && (
            <>
              <SubHeader>{t('editor.sidebar.relationships.parents')}</SubHeader>
              <Divider
                variant="fullWidth"
                sx={{ borderColor: theme.palette.brand.backgroundDialog }}
              />
              <List>
                {parents.map(parent => (
                  <StyledListItem key={parent.id}>
                    <ListItemText primary={parent.title} />
                    <Tooltip title={t('editor.sidebar.relationships.edit')}>
                      <StyledIconButton
                        size="small"
                        onClick={() => handleEditInitiative(parent.id!)}
                      >
                        <PencilIcon />
                      </StyledIconButton>
                    </Tooltip>
                    <Tooltip title={t('editor.sidebar.relationships.unlink')}>
                      <StyledIconButton
                        size="small"
                        onClick={() =>
                          removeParent({
                            initiativeId,
                            parentId: parent.id!,
                          })
                        }
                        disabled={isRemovingParent}
                      >
                        <BinIcon />
                      </StyledIconButton>
                    </Tooltip>
                  </StyledListItem>
                ))}
              </List>
            </>
          )}

          {/* Children List */}
          {children && children.length > 0 && (
            <>
              <SubHeader>{t('editor.sidebar.relationships.children')}</SubHeader>
              <Divider
                variant="fullWidth"
                sx={{ borderColor: theme.palette.brand.backgroundDialog }}
              />
              <List>
                {children.map(child => (
                  <StyledListItem key={child.id}>
                    <ListItemText primary={child.title} />
                    <Tooltip title={t('editor.sidebar.relationships.edit')}>
                      <StyledIconButton
                        size="small"
                        onClick={() => handleEditInitiative(child.id!)}
                      >
                        <PencilIcon />
                      </StyledIconButton>
                    </Tooltip>
                    <Tooltip title={t('editor.sidebar.relationships.unlink')}>
                      <StyledIconButton
                        size="small"
                        onClick={() =>
                          removeChild({
                            childId: child.id!,
                          })
                        }
                        disabled={isRemovingChild}
                      >
                        <BinIcon />
                      </StyledIconButton>
                    </Tooltip>
                  </StyledListItem>
                ))}
              </List>
            </>
          )}
        </>
      )}
    </SectionContainer>
  );
};

const SectionContainer = styled.div`
  margin-top: 2rem;
`;

const SectionHeader = styled.div`
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 1rem 0;
  font-weight: 600;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const SubHeader = styled.div`
  margin: 1.5rem 0 0.5rem;
  padding-left: 1rem;
  font-weight: 500;
  font-size: 14px;
  color: ${({ theme }) => theme.palette.brand.textPrimary};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
`;

const StyledRelationshipSelect = styled(Select)`
  width: 100%;
  font-size: 1.25rem;

  .MuiSelect-select {
    padding: 0.75rem 1rem;
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 6px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StyledListItem = styled(ListItem)`
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 1rem;
  padding-right: 0;

  .MuiListItemText-primary {
    font-size: 14px;
  }
`;

export { RelationshipsSection };
